import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  feature: 'bg-[#002FA7]/10 text-[#002FA7]',
  improvement: 'bg-[#AF52DE]/10 text-[#AF52DE]',
  fix: 'bg-[#34C759]/10 text-[#34C759]',
  security: 'bg-[#FF3B30]/10 text-[#FF3B30]',
  announcement: 'bg-[#FF9500]/10 text-[#FF9500]',
};

const RecentlyShippedCard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/changelog`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 3 },
        });
        setEntries(r.data.entries || []);
      } catch { /* silent — dashboard shouldn't fail if changelog is down */ }
      finally { setLoaded(true); }
    })();
  }, [token]);

  if (loaded && entries.length === 0) return null; // hide the card entirely if nothing to show

  return (
    <Card className="border-[#E5E7EB]" data-testid="recently-shipped-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#002FA7]" />Recently Shipped
        </CardTitle>
        <button
          onClick={() => navigate('/whats-new')}
          className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-0.5"
          data-testid="recently-shipped-see-all"
        >
          See all <ArrowRight className="w-3 h-3" />
        </button>
      </CardHeader>
      <CardContent className="space-y-2">
        {!loaded && <p className="text-[11px] text-[#9CA3AF]">Loading…</p>}
        {entries.map(e => (
          <button
            key={e.entry_id}
            onClick={() => navigate(`/whats-new#${e.entry_id}`)}
            className="w-full text-left p-2.5 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7]/40 hover:bg-[#FAFAFA] transition-colors"
            data-testid={`recently-shipped-entry-${e.entry_id}`}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className={`text-[9px] ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.feature}`}>{e.category}</Badge>
              <span className="text-[10px] text-[#9CA3AF] ml-auto">
                {e.published_at ? formatDistanceToNow(new Date(e.published_at), { addSuffix: true }) : ''}
              </span>
            </div>
            <p className="text-xs font-semibold text-[#0A0A0A] mt-1 leading-tight">{e.title}</p>
            <p className="text-[11px] text-[#4B5563] mt-0.5 line-clamp-2">{e.summary}</p>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};

export default RecentlyShippedCard;
